/**
 * Biblioteca de Job Descriptions existentes (Sol 30/09/2026).
 *
 * El agente de intake arma las JD nuevas; las que ya existen viven en documentos
 * sueltos de cada líder. Acá están juntas: la lista de puestos del mapa de
 * comunidad y, en cada uno, la JD pegada como texto. Decisión de Sol: solo TA,
 * todos los puestos, y se pega el texto — no se suben archivos.
 *
 * Cada guardado es una versión: el historial hace de borrador y no hay edición
 * destructiva.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, FileText, History, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { usePagination } from '@/hooks/usePagination';
import { trpc } from '@/lib/trpc';
import { toastError } from '@/lib/toast-error';
import { formatDateTime } from '@/lib/utils';
import { foldKey } from '@/../../shared/text-normalize';
import type { TaJdLibraryItem } from '@/../../shared/types/ta-metrics';

const k = 'talent_acquisition.jd_library';

function EditorDialog({ item, onClose }: { item: TaJdLibraryItem | null; onClose: () => void }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [texto, setTexto] = useState('');
  const [verHistorial, setVerHistorial] = useState(false);
  const clave = item?.clave ?? '';

  const versions = trpc.taMetrics.jdLibrary.versions.useQuery({ clave }, { enabled: !!clave });
  // El texto se carga una sola vez, cuando llegan las versiones: si se pisara en
  // cada render se le borraría lo que la persona está escribiendo.
  const [cargado, setCargado] = useState('');
  const vigente = versions.data?.length ? versions.data[versions.data.length - 1] : null;
  if (versions.data && cargado !== clave) {
    setCargado(clave);
    setTexto(vigente?.texto ?? '');
  }

  const save = trpc.taMetrics.jdLibrary.save.useMutation({
    onSuccess: () => {
      toast.success(t(`${k}.saved`));
      utils.taMetrics.jdLibrary.versions.invalidate({ clave });
      utils.taMetrics.jdLibrary.list.invalidate();
      onClose();
    },
    onError: (e) => toastError(e, t(`${k}.save_failed`)),
  });

  const sinCambios = texto.trim() === (vigente?.texto ?? '').trim();

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) { setCargado(''); onClose(); } }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-brand-green" />
            {item?.puesto}
          </DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t(`${k}.people_line`, { count: item.personas })}
              {item.areas.length > 0 && ` · ${item.areas.join(', ')}`}
            </p>

            {versions.isLoading && <div className="flex justify-center py-10"><WinclapLoader size={28} /></div>}
            {versions.error && (
              <Alert variant="destructive">
                <AlertTitle>{t(`${k}.load_error`)}</AlertTitle>
                <AlertDescription>{versions.error.message}</AlertDescription>
              </Alert>
            )}

            {versions.data && (
              <>
                <Textarea
                  id="ta-jd-library-texto"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder={t(`${k}.placeholder`)}
                  className="min-h-[340px] font-mono text-xs leading-relaxed"
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    {vigente
                      ? t(`${k}.current_line`, { n: vigente.n, at: formatDateTime(vigente.en), by: vigente.por })
                      : t(`${k}.never_saved`)}
                  </span>
                  <div className="flex items-center gap-2">
                    {versions.data.length > 1 && (
                      <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setVerHistorial((v) => !v)}>
                        <History className="h-3.5 w-3.5" />
                        {t(`${k}.history`, { count: versions.data.length })}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="gap-1.5"
                      disabled={save.isPending || sinCambios || !texto.trim()}
                      onClick={() => save.mutate({ clave, texto })}
                    >
                      {save.isPending ? <WinclapLoader size={14} /> : <Check className="h-3.5 w-3.5" />}
                      {t(`${k}.save`)}
                    </Button>
                  </div>
                </div>

                {verHistorial && (
                  <div className="space-y-2 border-t pt-3">
                    {[...versions.data].reverse().map((v) => (
                      <details key={v.n} className="rounded-lg border bg-muted/30 p-2">
                        <summary className="cursor-pointer text-xs font-medium">
                          {t(`${k}.version_line`, { n: v.n, at: formatDateTime(v.en), by: v.por })}
                        </summary>
                        <pre className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap font-sans text-xs">{v.texto}</pre>
                      </details>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TaJdLibraryTab() {
  const { t } = useTranslation();
  const [busqueda, setBusqueda] = useState('');
  const [soloSinCargar, setSoloSinCargar] = useState(false);
  const [abierto, setAbierto] = useState<TaJdLibraryItem | null>(null);
  const lista = trpc.taMetrics.jdLibrary.list.useQuery(undefined, { staleTime: 60_000 });

  const filtrados = useMemo(() => {
    const q = foldKey(busqueda);
    return (lista.data ?? []).filter((p) => {
      if (soloSinCargar && p.versiones > 0) return false;
      if (!q) return true;
      return foldKey(p.puesto).includes(q) || p.areas.some((a) => foldKey(a).includes(q));
    });
  }, [lista.data, busqueda, soloSinCargar]);

  const { pageItems, footer } = usePagination(filtrados, 25, `${busqueda}|${soloSinCargar}`);
  const cargados = (lista.data ?? []).filter((p) => p.versiones > 0).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t(`${k}.intro`, { loaded: cargados, total: lista.data?.length ?? 0 })}
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={soloSinCargar ? 'default' : 'outline'} onClick={() => setSoloSinCargar((v) => !v)}>
            {t(`${k}.only_missing`)}
          </Button>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="ta-jd-library-buscar"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t(`${k}.search`)}
              className="h-9 w-56 pl-8"
            />
          </div>
        </div>
      </div>

      {lista.isLoading && <div className="flex justify-center py-24"><WinclapLoader size={40} /></div>}
      {lista.error && (
        <Alert variant="destructive">
          <AlertTitle>{t(`${k}.load_error`)}</AlertTitle>
          <AlertDescription>{lista.error.message}</AlertDescription>
        </Alert>
      )}

      {lista.data && (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t(`${k}.col_position`)}</TableHead>
                  <TableHead>{t(`${k}.col_areas`)}</TableHead>
                  <TableHead className="text-right">{t(`${k}.col_people`)}</TableHead>
                  <TableHead>{t(`${k}.col_status`)}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((p) => (
                  <TableRow key={p.clave} className="cursor-pointer" onClick={() => setAbierto(p)}>
                    <TableCell className="font-medium">{p.puesto}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.areas.join(', ') || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.personas}</TableCell>
                    <TableCell>
                      {p.versiones > 0 ? (
                        <Badge variant="outline" className="border-brand-green/40 text-brand-green">
                          {t(`${k}.loaded_at`, { at: p.actualizada ? formatDateTime(p.actualizada) : '' })}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">{t(`${k}.missing`)}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setAbierto(p); }}>
                        {p.versiones > 0 ? t(`${k}.open`) : t(`${k}.add`)}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {pageItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      {t(`${k}.empty`)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {footer}
        </>
      )}

      <EditorDialog item={abierto} onClose={() => setAbierto(null)} />
    </div>
  );
}
